
import { Component, Watch } from "vue-property-decorator";
import {
  EpgItem,
  Episode,
  Movie,
  Season
} from "@/services/apiService/api.types";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import SpatialNavigation from "spatial-navigation-js";
import { Action, Getter, Mutation } from "vuex-class";
import { Namespace } from "@/store/types";
import {
  ChannelEpgParams,
  DailyEpg,
  OverlayAction,
  OverlayGetter,
  OverlayMutation
} from "@/components/overlay/types";
import { EpgHandler } from "@/mixins/EpgHandler";
import { mixins } from "vue-class-component";
import dayjs from "dayjs";
import { ChannelChangeAction } from "@/components/channelChange/types";
import { getEpgDates, isEpisode } from "@/helpers/utils";
import { ChannelsPageGetter } from "@/components/channelsPage/types";
import { VodMetaDataHandler } from "@/mixins/VodMetaDataHandler";
import { EventBus } from "@/main";
import { PlayerControlsEnum } from "@/helpers/constants";

@Component({})
export default class OverlayContentList extends mixins(
  EpgHandler,
  VodMetaDataHandler
) {
  @Action(OverlayAction.UpdateChannelEpg, {
    namespace: Namespace.Overlay
  })
  updateChannelEpg!: (channelEpgParams?: ChannelEpgParams) => Promise<void>;

  @Action(ChannelChangeAction.UpdateOffset, {
    namespace: Namespace.ChannelChange
  })
  updateOffset!: (offset: number) => void;

  @Getter(ChannelsPageGetter.getActiveInfoScreenEpgItem, {
    namespace: Namespace.ChannelsPage
  })
  getActiveInfoScreenEpgItem!: EpgItem | undefined;

  @Getter(OverlayGetter.GetTvShowMeta, {
    namespace: Namespace.Overlay
  })
  getTvShowMeta!: Season[] | null;

  @Mutation(OverlayMutation.SetActiveVodItem, {
    namespace: Namespace.Overlay
  })
  setActiveVodItem!: (vod: Movie | Episode | null) => void;

  contentLists: DailyEpg[] | Season[] = []; //listing organised chunks of content
  activeContentIndex = 6; //index of the day/season whose content is shown

  KeySet = KeySet;

  focusedContentItemIndex = 0;

  async mounted(): Promise<void> {
    if (this.getTvShowMeta !== null) {
      this.contentLists = this.getTvShowMeta;
      this.activeContentIndex = this.setActiveContentListIndex();
      //setting initial index to currently active episode
      this.getTvShowMeta.forEach((season) => {
        season.episodes.forEach((episode, index) => {
          if (
            this.getActiveVodItem &&
            episode.id === this.getActiveVodItem.id
          ) {
            this.focusedContentItemIndex = index;
          }
        });
      });
    } else {
      this.contentLists = getEpgDates();
      this.activeContentIndex = this.setActiveContentListIndex();
      this.updateActiveEpgItem();
      //setting initial index to currently active epg item
      this.focusedContentItemIndex = this.getChannelEpg.findIndex(
        (item) => item.id === this.getActiveEpgItem.id
      );
      setInterval(() => {
        this.secondsElapsed++;
      }, 1000);
    }
    setTimeout(() => {
      SpatialNavigation.focus("#epgList");
    }, 0);
  }
  changeContentList(direction: KeySet): void {
    if (direction === KeySet.KEY_LEFT && this.activeContentIndex > 0) {
      this.activeContentIndex--;
    }

    if (
      direction === KeySet.KEY_RIGHT &&
      this.activeContentIndex < this.contentLists.length - 1
    ) {
      this.activeContentIndex++;
    }
  }

  listContentItems(direction: KeySet): void {
    if (direction === KeySet.KEY_UP && this.focusedContentItemIndex > 0) {
      this.focusedContentItemIndex--;
    }
    if (direction === KeySet.KEY_DOWN) {
      if (
        this.getTvShowMeta &&
        this.focusedContentItemIndex <
          this.getTvShowMeta[this.activeContentIndex].episodes.length - 1
      ) {
        this.focusedContentItemIndex++;
        return;
      }

      if (
        !this.getTvShowMeta &&
        this.focusedContentItemIndex < this.getChannelEpg?.length - 1
      )
        this.focusedContentItemIndex++;
    }
  }

  //set offset of desired epgItem thus triggering player to play the required show
  playContentItem(contentItem: EpgItem | Episode): void {
    if (isEpisode(contentItem)) {
      this.setActiveVodItem(contentItem);
      EventBus.$emit(PlayerControlsEnum.Start);
      this.focusActiveContentItem(contentItem);
    } else {
      if (
        this.getChannelEpg &&
        dayjs(contentItem.epgStarttime).isBefore(dayjs())
      ) {
        const offset = dayjs().diff(contentItem.epgStarttime, "second");
        this.updateOffset(offset);
        this.focusActiveContentItem(contentItem);
      }
    }
  }

  //used to set clicked epg item as active
  focusActiveContentItem(contentItemToFocus: EpgItem | Episode): void {
    if (isEpisode(contentItemToFocus)) {
      this.getTvShowMeta![this.activeContentIndex].episodes.forEach(
        (episode, index) => {
          if (episode.id === contentItemToFocus.id) {
            this.focusedContentItemIndex = index;
          }
        }
      );
    } else {
      this.getChannelEpg.forEach((epgItem, index) => {
        if (epgItem.id === contentItemToFocus.id) {
          this.focusedContentItemIndex = index;
        }
      });
    }
  }

  //sets index for the date of active epgList
  setActiveContentListIndex(): number {
    if (this.getTvShowMeta) {
      return (this.contentLists as Season[]).findIndex(
        (season: Season) =>
          season.id === (this.getActiveVodItem as Episode).season_id
      );
    }
    return (this.contentLists as DailyEpg[]).findIndex((epgDate: DailyEpg) =>
      dayjs(epgDate.date).isSame(
        dayjs().subtract(this.getOffset, "second"),
        "day"
      )
    );
  }

  //content items from selected day/season that are being shown
  get contentItemsShown(): EpgItem[] | Episode[] {
    if (this.getTvShowMeta !== null) {
      return this.getTvShowMeta[this.activeContentIndex].episodes.filter(
        (episode, index) => {
          return (
            index >= this.focusedContentItemIndex - 2 &&
            index <= this.focusedContentItemIndex
          );
        }
      );
    }
    return this.getChannelEpg.filter((epgItem, index) => {
      return (
        index >= this.focusedContentItemIndex - 2 &&
        index <= this.focusedContentItemIndex
      );
    });
  }

  get arrowLeft(): string {
    return require("/src/assets/svg/left.svg");
  }

  get arrowRight(): string {
    return require("/src/assets/svg/right.svg");
  }

  contentListActions(event: KeyboardEvent): void {
    //must be included in order to "lock" the currently focused element/prevent SpatialNavigation events from firing and refocusing
    SpatialNavigation.pause();
    switch (keyCodeFinder(event.keyCode)) {
      case KeySet.KEY_LEFT:
        this.changeContentList(KeySet.KEY_LEFT);
        break;
      case KeySet.KEY_RIGHT:
        this.changeContentList(KeySet.KEY_RIGHT);
        break;
      case KeySet.KEY_UP:
        this.listContentItems(KeySet.KEY_UP);
        this.setActiveEpgItem(this.getChannelEpg[this.focusedContentItemIndex]);
        break;
      case KeySet.KEY_DOWN:
        this.listContentItems(KeySet.KEY_DOWN);
        this.setActiveEpgItem(this.getChannelEpg[this.focusedContentItemIndex]);
        break;
      case KeySet.KEY_ENTER:
        if (
          this.getTvShowMeta &&
          isEpisode(
            this.getTvShowMeta[this.activeContentIndex].episodes[
              this.focusedContentItemIndex
            ]
          )
        ) {
          this.playContentItem(
            this.getTvShowMeta[this.activeContentIndex].episodes[
              this.focusedContentItemIndex
            ]
          );
        }
        this.playContentItem(this.getChannelEpg[this.focusedContentItemIndex]);
        break;
    }
  }

  contentItemThumbnail(contentItem: EpgItem | Episode): string {
    if (isEpisode(contentItem)) {
      return contentItem.media ? contentItem.media.url : this.imgPlaceholder;
    }
    return contentItem.image ? contentItem.image : this.imgPlaceholder;
  }

  @Watch("activeEpgIndex")
  handleEpgListChange(): void {
    this.updateChannelEpg({
      date: (this.contentLists as DailyEpg[])[this.activeContentIndex].date
    }).then(() => {
      const tempEpgItemIndex = this.getChannelEpg.findIndex(
        (item) => item.id === this.getActiveEpgItem.id
      );
      //used for focusing correct item when changing dates on this component(epgList with playing item focuses the active epg item)
      if (tempEpgItemIndex === -1) {
        this.focusedContentItemIndex = 0;
        return;
      }
      this.focusedContentItemIndex = tempEpgItemIndex;
    });
  }
}
